import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { CodeMonkey } from '../components/CodeMonkey';
import { CodeMonkeyLinks, CodeMonkeyAnalytics, CodeMonkeyTrophy, CodeMonkeyRocket, CodeMonkeyDollar } from '../components/CodeMonkeyVariations';
import blogPosts from '../data/blogPosts';

// Map different monkey variations to posts
const getMonkeyForPost = (index) => {
  const monkeys = [
    CodeMonkey,
    CodeMonkeyLinks,
    CodeMonkeyAnalytics,
    CodeMonkeyTrophy,
    CodeMonkeyRocket,
    CodeMonkeyDollar
  ];
  const MonkeyComponent = monkeys[index % monkeys.length];
  return <MonkeyComponent size={180} />;
};

const Blog = () => {
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "SEO & Link Building Masterclass",
    "description": "Comprehensive educational content on SEO, link building, domain authority, and the tokenization of SEO equity through FLUMBERICO.",
    "provider": {
      "@type": "Organization",
      "name": "FLUMBERICO Team",
      "url": "https://www.flumberico.fun"
    },
    "educationalLevel": "Intermediate to Advanced",
    "about": [
      "SEO",
      "Link Building",
      "Domain Authority",
      "Backlinks",
      "SEO Tokenization"
    ],
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "PT2H"
    }
  };

  const blogListingSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "FLUMBERICO Blog",
    "description": "Expert insights on SEO, link building, domain authority, and the world's first tokenization of SEO equity.",
    "url": "https://www.flumberico.fun/blog",
    "publisher": {
      "@type": "Organization",
      "name": "FLUMBERICO Team",
      "url": "https://www.flumberico.fun"
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `https://www.flumberico.fun/blog/${post.slug}`,
      "datePublished": "2024-10-20",
      "author": {
        "@type": "Organization",
        "name": "FLUMBERICO Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "FLUMBERICO Team"
      },
      "description": post.excerpt
    }))
  };

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <Helmet>
        <link rel="canonical" href="https://www.flumberico.fun/blog" />
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(blogListingSchema)}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <BookOpen className="w-20 h-20 text-gold mx-auto mb-6" />
          <h1 className="text-6xl font-black mb-6 text-gold">
            FLUMBERICO Blog
          </h1>
          <p className="text-2xl text-white font-semibold mb-4">Insights on SEO, Tokenization & Link Equity</p>
          <p className="text-lg text-white/70">
            Master SEO, link building, and domain authority with expert insights from the flumberico community
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              className="group bg-gradient-to-br from-gold/10 to-black/50 border border-gold/30 rounded-lg overflow-hidden hover:border-gold/60 transition-all hover:transform hover:scale-[1.02]"
            >
              {/* Thumbnail with SVG */}
              <div className="relative h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
                <div className="relative z-10">
                  {getMonkeyForPost(index)}
                </div>
                <div className="absolute bottom-3 right-3 bg-gold text-black px-3 py-1 rounded-full font-bold text-xs">
                  {post.category}
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 text-silver text-xs mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} /> {post.date}
                  </span>
                  <span>•</span>
                  <span>{post.readTime}</span>
                </div>
                
                <h2 className="text-xl font-bold text-gold mb-3 group-hover:text-silver transition-colors line-clamp-2">
                  {post.title}
                </h2>
                
                <p className="text-white/80 text-sm leading-relaxed mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex items-center text-gold font-semibold text-sm group-hover:gap-2 transition-all">
                  Read Article <ArrowRight size={16} className="ml-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Blog;